import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Crown, Clock, Check, Loader2, ExternalLink, Wallet, DollarSign, CreditCard } from 'lucide-react';
import { toast } from 'sonner';

export default function CustomerMemberships() {
  const [data, setData] = useState({ membership: null, available_plans: [] });
  const [affiliate, setAffiliate] = useState({ affiliate: null, commissions: [], withdrawals: [] });
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(false);
  const [showSubscribeDialog, setShowSubscribeDialog] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showWithdrawDialog, setShowWithdrawDialog] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState(0);
  const [withdrawing, setWithdrawing] = useState(false);
  const [bankForm, setBankForm] = useState({ bank_name: '', bank_account_number: '', bank_account_name: '' });
  const [savingBank, setSavingBank] = useState(false);

  const fetchData = async () => {
    try {
      const [membershipRes, affiliateRes, withdrawalsRes] = await Promise.all([
        api.get('/customer/membership'),
        api.get('/customer/affiliate'),
        api.get('/customer/affiliate/withdrawals').catch(() => ({ data: { withdrawals: [] } }))
      ]);
      setData(membershipRes.data);
      setAffiliate({
        ...affiliateRes.data,
        withdrawals: withdrawalsRes.data.withdrawals || []
      });
      if (affiliateRes.data.affiliate) {
        setBankForm({
          bank_name: affiliateRes.data.affiliate.bank_name || '',
          bank_account_number: affiliateRes.data.affiliate.bank_account_number || '',
          bank_account_name: affiliateRes.data.affiliate.bank_account_name || ''
        });
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleSubscribe = async () => {
    if (!selectedPlan) return;
    setSubscribing(true);
    try {
      const res = await api.post(`/customer/membership/subscribe/${selectedPlan.id}`);
      if (res.data.payment_url) {
        window.open(res.data.payment_url, '_blank');
        toast.success('Mengarahkan ke halaman pembayaran...');
      }
      setShowSubscribeDialog(false);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal subscribe');
    } finally {
      setSubscribing(false);
    }
  };

  const handleSaveBank = async () => {
    if (!bankForm.bank_name || !bankForm.bank_account_number || !bankForm.bank_account_name) {
      toast.error('Lengkapi semua data bank');
      return;
    }
    setSavingBank(true);
    try {
      await api.put('/customer/affiliate/bank', bankForm);
      toast.success('Data bank disimpan');
      fetchData();
    } catch (e) {
      toast.error('Gagal menyimpan data bank');
    } finally {
      setSavingBank(false);
    }
  };

  const handleWithdraw = async () => {
    if (withdrawAmount < 1000) {
      toast.error('Minimum withdrawal Rp 1.000');
      return;
    }
    setWithdrawing(true);
    try {
      await api.post('/customer/affiliate/withdraw', { amount: withdrawAmount });
      toast.success('Permintaan withdrawal berhasil dikirim');
      setShowWithdrawDialog(false);
      setWithdrawAmount(0);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal request withdrawal');
    } finally {
      setWithdrawing(false);
    }
  };

  const fmt = (v) => new Intl.NumberFormat('id-ID').format(v || 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--store-primary, #2D6CDF)]" />
      </div>
    );
  }

  const { membership, available_plans } = data;
  const aff = affiliate.affiliate;

  return (
    <div data-testid="customer-memberships-page">
      <h1 className="text-2xl font-bold tracking-tight text-slate-900 mb-6" style={{ fontFamily: 'Plus Jakarta Sans' }}>Membership & Affiliate</h1>

      <Tabs defaultValue="membership">
        <TabsList>
          <TabsTrigger value="membership">Membership</TabsTrigger>
          <TabsTrigger value="affiliate">Affiliate</TabsTrigger>
        </TabsList>

        <TabsContent value="membership" className="mt-6">
          {/* Current Membership */}
          {membership && membership.status === 'active' ? (
            <Card className="border-slate-200 shadow-sm mb-6 bg-gradient-to-br from-violet-50 to-purple-50">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
                    <Crown size={28} className="text-white" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="text-xl font-bold text-slate-900">{membership.plan?.name || membership.plan_name}</h3>
                      <Badge className="bg-emerald-100 text-emerald-700">Active</Badge>
                    </div>
                    <p className="text-sm text-slate-600 mt-1">Level {membership.level}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-slate-500">Berakhir</p>
                    <p className="font-semibold text-slate-900">
                      {membership.expires_at ? new Date(membership.expires_at).toLocaleDateString('id-ID') : '-'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="border-slate-200 shadow-sm mb-6">
              <CardContent className="py-8 text-center">
                <Crown size={48} className="mx-auto text-slate-300 mb-4" />
                <h3 className="font-semibold text-slate-900 mb-2">Belum punya membership</h3>
                <p className="text-sm text-slate-500">Pilih membership plan untuk akses konten eksklusif</p>
              </CardContent>
            </Card>
          )}

          {/* Available Plans */}
          <h3 className="font-semibold text-slate-900 mb-4">Pilih Membership Plan</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {available_plans.map((plan) => {
              const isCurrentPlan = membership?.plan_id === plan.id && membership?.status === 'active';
              return (
                <Card key={plan.id} className={`border-slate-200 shadow-sm ${isCurrentPlan ? 'ring-2 ring-violet-500' : ''}`}>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-3">
                      <Crown size={24} className="text-violet-500" />
                      {isCurrentPlan && <Badge className="bg-violet-100 text-violet-700">Current</Badge>}
                    </div>
                    <h4 className="text-lg font-bold text-slate-900">{plan.name}</h4>
                    <p className="text-sm text-slate-600 mt-1 line-clamp-2">{plan.description || 'No description'}</p>
                    <p className="text-2xl font-bold text-[var(--store-primary, #2D6CDF)] mt-3">
                      Rp {fmt(plan.price)}
                      <span className="text-sm text-slate-500 font-normal">/{plan.duration_days} hari</span>
                    </p>
                    {plan.features?.length > 0 && (
                      <ul className="mt-3 space-y-1">
                        {plan.features.slice(0, 3).map((f, i) => (
                          <li key={i} className="flex items-center gap-2 text-sm text-slate-600">
                            <Check size={14} className="text-emerald-500" /> {f}
                          </li>
                        ))}
                      </ul>
                    )}
                    <Button
                      onClick={() => { setSelectedPlan(plan); setShowSubscribeDialog(true); }}
                      disabled={isCurrentPlan}
                      className="w-full mt-4 bg-[var(--store-primary, #2D6CDF)] hover:bg-[var(--store-primary-hover, #1A5BC4)]"
                      data-testid={`subscribe-plan-${plan.id}`}
                    >
                      {isCurrentPlan ? 'Current Plan' : 'Subscribe'}
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
            {available_plans.length === 0 && (
              <div className="col-span-full py-12 text-center text-slate-400">
                Tidak ada membership plan tersedia
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="affiliate" className="mt-6">
          {aff ? (
            <div className="space-y-6">
              {/* Affiliate Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="border-slate-200 shadow-sm">
                  <CardContent className="p-4">
                    <p className="text-2xl font-bold text-slate-900">Rp {fmt(aff.balance)}</p>
                    <p className="text-xs text-slate-500">Balance</p>
                  </CardContent>
                </Card>
                <Card className="border-slate-200 shadow-sm">
                  <CardContent className="p-4">
                    <p className="text-2xl font-bold text-slate-900">Rp {fmt(aff.total_earned)}</p>
                    <p className="text-xs text-slate-500">Total Earned</p>
                  </CardContent>
                </Card>
                <Card className="border-slate-200 shadow-sm">
                  <CardContent className="p-4">
                    <p className="text-2xl font-bold text-slate-900">{aff.total_orders || 0}</p>
                    <p className="text-xs text-slate-500">Orders</p>
                  </CardContent>
                </Card>
                <Card className="border-slate-200 shadow-sm">
                  <CardContent className="p-4">
                    <p className="text-2xl font-bold text-slate-900">{aff.commission_rate}%</p>
                    <p className="text-xs text-slate-500">Rate</p>
                  </CardContent>
                </Card>
              </div>

              {/* Referral Code */}
              <Card className="border-slate-200 shadow-sm">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">Kode Referral Anda</p>
                      <p className="text-xl font-mono font-bold text-[var(--store-primary, #2D6CDF)]">{aff.code}</p>
                    </div>
                    <Badge variant="outline" className={aff.status === 'approved' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
                      {aff.status}
                    </Badge>
                  </div>
                </CardContent>
              </Card>

              {/* Bank Details */}
              <Card className="border-slate-200 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <CreditCard size={18} /> Rekening Bank
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label>Nama Bank</Label>
                      <Input
                        value={bankForm.bank_name}
                        onChange={(e) => setBankForm({ ...bankForm, bank_name: e.target.value })}
                        placeholder="BCA, BNI, Mandiri, dll"
                        data-testid="bank-name-input"
                      />
                    </div>
                    <div>
                      <Label>No. Rekening</Label>
                      <Input
                        value={bankForm.bank_account_number}
                        onChange={(e) => setBankForm({ ...bankForm, bank_account_number: e.target.value })}
                        placeholder="1234567890"
                        data-testid="bank-account-number-input"
                      />
                    </div>
                    <div>
                      <Label>Nama Pemilik</Label>
                      <Input
                        value={bankForm.bank_account_name}
                        onChange={(e) => setBankForm({ ...bankForm, bank_account_name: e.target.value })}
                        placeholder="Nama sesuai rekening"
                        data-testid="bank-account-name-input"
                      />
                    </div>
                  </div>
                  <Button onClick={handleSaveBank} disabled={savingBank} variant="outline" data-testid="save-bank-btn">
                    {savingBank && <Loader2 size={14} className="animate-spin mr-2" />}
                    Simpan Data Bank
                  </Button>
                </CardContent>
              </Card>

              {/* Withdraw Button */}
              {aff.status === 'approved' && (
                <Card className="border-slate-200 shadow-sm">
                  <CardContent className="p-4 flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-slate-900">Tarik Saldo</p>
                      <p className="text-sm text-slate-500">Balance: Rp {fmt(aff.balance)}</p>
                    </div>
                    <Button
                      onClick={() => setShowWithdrawDialog(true)}
                      disabled={aff.balance < 1000}
                      className="bg-emerald-500 hover:bg-emerald-600"
                      data-testid="withdraw-btn"
                    >
                      <Wallet size={16} className="mr-2" /> Withdraw
                    </Button>
                  </CardContent>
                </Card>
              )}

              {/* Withdrawal History */}
              <Card className="border-slate-200 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-base">Riwayat Withdrawal</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-100 bg-slate-50">
                        <th className="text-left py-3 px-4 text-slate-500 font-medium">Tanggal</th>
                        <th className="text-left py-3 px-4 text-slate-500 font-medium">Amount</th>
                        <th className="text-left py-3 px-4 text-slate-500 font-medium">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {affiliate.withdrawals.map((w) => (
                        <tr key={w.id} className="border-b border-slate-50">
                          <td className="py-3 px-4 text-slate-600">{new Date(w.created_at).toLocaleDateString('id-ID')}</td>
                          <td className="py-3 px-4 font-semibold">Rp {fmt(w.amount)}</td>
                          <td className="py-3 px-4">
                            <Badge variant="outline" className={
                              w.status === 'approved' ? 'bg-emerald-100 text-emerald-700' :
                              w.status === 'rejected' ? 'bg-red-100 text-red-700' :
                              'bg-amber-100 text-amber-700'
                            }>{w.status}</Badge>
                          </td>
                        </tr>
                      ))}
                      {affiliate.withdrawals.length === 0 && (
                        <tr><td colSpan={3} className="py-8 text-center text-slate-400">Belum ada withdrawal</td></tr>
                      )}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            </div>
          ) : (
            <Card className="border-slate-200 shadow-sm">
              <CardContent className="py-12 text-center">
                <DollarSign size={48} className="mx-auto text-slate-300 mb-4" />
                <h3 className="font-semibold text-slate-900 mb-2">Belum terdaftar sebagai Affiliate</h3>
                <p className="text-sm text-slate-500 mb-4">Daftar sebagai affiliate untuk mendapatkan komisi dari setiap penjualan</p>
                <Button
                  onClick={async () => {
                    try {
                      await api.post('/customer/affiliate/register');
                      toast.success('Berhasil mendaftar sebagai affiliate');
                      fetchData();
                    } catch (e) {
                      toast.error(e.response?.data?.detail || 'Gagal mendaftar');
                    }
                  }}
                  className="bg-[var(--store-primary, #2D6CDF)] hover:bg-[var(--store-primary-hover, #1A5BC4)]"
                  data-testid="register-affiliate-btn"
                >
                  Daftar Affiliate
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Subscribe Dialog */}
      <Dialog open={showSubscribeDialog} onOpenChange={setShowSubscribeDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Subscribe Membership</DialogTitle>
          </DialogHeader>
          {selectedPlan && (
            <div className="space-y-4">
              <div className="p-4 bg-violet-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <Crown size={24} className="text-violet-600" />
                  <div>
                    <p className="font-semibold text-slate-900">{selectedPlan.name}</p>
                    <p className="text-sm text-slate-600">{selectedPlan.duration_days} hari</p>
                  </div>
                </div>
                <p className="text-2xl font-bold text-[var(--store-primary, #2D6CDF)] mt-3">Rp {fmt(selectedPlan.price)}</p>
              </div>
              <p className="text-sm text-slate-600">
                Anda akan diarahkan ke halaman pembayaran. Membership akan aktif setelah pembayaran berhasil.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSubscribeDialog(false)}>Batal</Button>
            <Button onClick={handleSubscribe} disabled={subscribing} className="bg-[var(--store-primary, #2D6CDF)] hover:bg-[var(--store-primary-hover, #1A5BC4)]">
              {subscribing && <Loader2 size={14} className="animate-spin mr-2" />}
              Bayar Sekarang
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Withdraw Dialog */}
      <Dialog open={showWithdrawDialog} onOpenChange={setShowWithdrawDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Withdraw Saldo</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 bg-slate-50 rounded-lg">
              <p className="text-sm text-slate-500">Saldo tersedia</p>
              <p className="text-2xl font-bold text-emerald-600">Rp {fmt(aff?.balance)}</p>
            </div>
            <div>
              <Label>Jumlah Withdraw</Label>
              <Input
                type="number"
                value={withdrawAmount}
                onChange={(e) => setWithdrawAmount(parseInt(e.target.value) || 0)}
                placeholder="Masukkan jumlah"
                data-testid="withdraw-amount-input"
              />
            </div>
            {(!bankForm.bank_name || !bankForm.bank_account_number) && (
              <p className="text-sm text-red-500">Lengkapi data bank terlebih dahulu</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowWithdrawDialog(false)}>Batal</Button>
            <Button
              onClick={handleWithdraw}
              disabled={withdrawing || !bankForm.bank_name || !bankForm.bank_account_number}
              className="bg-emerald-500 hover:bg-emerald-600"
            >
              {withdrawing && <Loader2 size={14} className="animate-spin mr-2" />}
              Request Withdraw
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
